import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { FilterList } from '../filter/FilterList';
import {
  buildPendingFilters,
  extractSelectedFiltersFromPending,
  togglePendingFilterOption,
  togglePendingFilterSection,
} from '../../modules/blog-filter-utils';

let lockedScrollY = 0;
let touchMoveBlocker = null;

function isInsidePanelScroller(target) {
  return !!(target && target.closest && target.closest('.blog-mobile-filter-panel-scroll'));
}

function lockBodyScroll() {
  lockedScrollY = window.scrollY || window.pageYOffset || 0;

  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';

  if (!touchMoveBlocker) {
    touchMoveBlocker = (event) => {
      if (!isInsidePanelScroller(event.target)) {
        event.preventDefault();
      }
    };
    document.addEventListener('touchmove', touchMoveBlocker, { passive: false });
  }
}

function unlockBodyScroll(restoreScroll) {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';

  if (touchMoveBlocker) {
    document.removeEventListener('touchmove', touchMoveBlocker);
    touchMoveBlocker = null;
  }

  if (restoreScroll) {
    window.scrollTo(0, lockedScrollY);
  }
}

class BlogMobileFilterPanel extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pendingFilters: buildPendingFilters(props.filters, props.selectedFilters),
    };

    this.panelEl = null;
    this.skipScrollRestore = false;
    this.handleClose = this.handleClose.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handlePendingButtonClick = this.handlePendingButtonClick.bind(this);
    this.handlePendingFilterClick = this.handlePendingFilterClick.bind(this);
    this.setPanelRef = this.setPanelRef.bind(this);
    this.syncPanelToVisualViewport = this.syncPanelToVisualViewport.bind(this);
  }

  componentDidMount() {
    if (this.props.isOpen) {
      lockBodyScroll();
      this.attachViewportListeners();
      this.syncPanelToVisualViewport();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      this.skipScrollRestore = false;
      this.syncPendingFromApplied();
      lockBodyScroll();
      this.attachViewportListeners();
      this.syncPanelToVisualViewport();
    }

    if (prevProps.isOpen && !this.props.isOpen) {
      this.detachViewportListeners();
      this.clearPanelViewportStyles();
      const shouldRestore = !this.skipScrollRestore;
      this.skipScrollRestore = false;
      unlockBodyScroll(shouldRestore);
    }
  }

  componentWillUnmount() {
    this.detachViewportListeners();
    this.clearPanelViewportStyles();
    unlockBodyScroll(false);
  }

  setPanelRef(el) {
    this.panelEl = el;
  }

  syncPanelToVisualViewport() {
    if (!this.panelEl) {
      return;
    }

    const vv = window.visualViewport;
    const top = vv ? vv.offsetTop : 0;
    const height = (vv && vv.height) || window.innerHeight;

    this.panelEl.style.top = `${top}px`;
    this.panelEl.style.height = `${height}px`;
    this.panelEl.style.bottom = 'auto';
  }

  clearPanelViewportStyles() {
    if (!this.panelEl) {
      return;
    }

    this.panelEl.style.top = '';
    this.panelEl.style.height = '';
    this.panelEl.style.bottom = '';
  }

  attachViewportListeners() {
    this.detachViewportListeners();
    window.addEventListener('resize', this.syncPanelToVisualViewport);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.syncPanelToVisualViewport);
      window.visualViewport.addEventListener('scroll', this.syncPanelToVisualViewport);
    }
  }

  detachViewportListeners() {
    window.removeEventListener('resize', this.syncPanelToVisualViewport);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.syncPanelToVisualViewport);
      window.visualViewport.removeEventListener('scroll', this.syncPanelToVisualViewport);
    }
  }

  syncPendingFromApplied() {
    const { filters, selectedFilters } = this.props;

    this.setState({
      pendingFilters: buildPendingFilters(filters, selectedFilters),
    });
  }

  handleClose() {
    this.skipScrollRestore = false;
    this.syncPendingFromApplied();
    this.props.onClose();
  }

  handleApply() {
    this.skipScrollRestore = true;
    const pendingSelectedFilters = extractSelectedFiltersFromPending(this.state.pendingFilters);
    this.props.onApply(pendingSelectedFilters);
  }

  handlePendingButtonClick(event, filterName) {
    event.nativeEvent.stopImmediatePropagation();

    this.setState(prevState => ({
      pendingFilters: togglePendingFilterSection(prevState.pendingFilters, filterName),
    }));
  }

  handlePendingFilterClick(option) {
    this.setState(prevState => ({
      pendingFilters: togglePendingFilterOption(prevState.pendingFilters, option),
    }));
  }

  render() {
    const { pendingFilters } = this.state;
    const { isOpen } = this.props;

    const panel = (
      <div
        ref={this.setPanelRef}
        className={`filter-toggle blog-mobile-filter-panel${isOpen ? ' toggled' : ''}`}
        aria-hidden={!isOpen}
      >
        <div className="blog-mobile-filter-panel-inner">
          <div className="blog-mobile-filter-panel-header">
            <h2 className="blog-mobile-filter-panel-title">filters</h2>
            <button
              type="button"
              className="blog-mobile-filter-panel-close"
              aria-label="Close filters"
              onClick={this.handleClose}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
              </svg>
            </button>
          </div>

          <div className="blog-mobile-filter-panel-scroll">
            <div className="filter blog-mobile-filter-list">
              <div className="filter-container">
                <FilterList
                  filters={pendingFilters}
                  filterClick={this.handlePendingFilterClick}
                  buttonClick={this.handlePendingButtonClick}
                />
              </div>
            </div>
          </div>

          <button
            type="button"
            className="blog-mobile-filter-apply btn btn-teal rounded-pill"
            onClick={this.handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    );

    if (typeof document !== 'undefined' && document.body) {
      return createPortal(panel, document.body);
    }

    return panel;
  }
}

BlogMobileFilterPanel.propTypes = {
  filters: PropTypes.shape(PropTypes.object),
  selectedFilters: PropTypes.shape(PropTypes.object),
  isOpen: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BlogMobileFilterPanel.defaultProps = {
  selectedFilters: {},
  isOpen: false,
};

export default BlogMobileFilterPanel;
